/**
 * Attendance Sheet & Certificate Generator — non-LLM templated tools.
 * /teaching/attendance     → roster CSV/manual → print-ready attendance sheet
 * /teaching/certificate    → bulk names → branded printable certificates
 */
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarCheck, Award, ArrowLeft, Printer, Plus, Trash2, Upload, Sparkles,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { PremiumCard, PremiumButton, SectionHeading, Pill } from "@/components/premium/Primitives";

function parseCSVNames(raw) {
  if (!raw) return [];
  return raw.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
}

// ─── Attendance Sheet ─────────────────────────────────────────────────
function AttendanceSheet() {
  const navigate = useNavigate();
  const [className, setClassName] = useState("Class 8A");
  const [subject, setSubject] = useState("Science");
  const [teacher, setTeacher] = useState("");
  const [month, setMonth] = useState(new Date().toLocaleString("default", { month: "long", year: "numeric" }));
  const [namesRaw, setNamesRaw] = useState("Aarav Sharma\nNeha Patel\nRohan Mehta\nPriya Singh\nKabir Joshi");
  const [days, setDays] = useState(26);

  const names = parseCSVNames(namesRaw);
  const dayList = Array.from({ length: Math.min(days, 31) }, (_, i) => i + 1);

  const printSheet = () => window.print();

  return (
    <div className="min-h-screen bg-white font-sans" data-testid="teaching-tool-attendance">
      <PremiumNav />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 print:py-2">
        <button
          onClick={() => navigate("/teaching")}
          className="print:hidden inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#A855F7] mb-6"
          data-testid="suite-back"
        >
          <ArrowLeft className="w-4 h-4"/> Back to Teaching Suite
        </button>

        <div className="print:hidden">
          <SectionHeading
            eyebrow={<><CalendarCheck className="w-3 h-3 inline -mt-0.5 mr-1"/> Teaching Suite</>}
            title="Attendance Sheet"
            subtitle="Class roster + days → instant printable monthly attendance sheet."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8 print:hidden">
          <PremiumCard className="p-6 lg:col-span-1" hover={false}>
            <h3 className="font-heading text-lg font-semibold mb-4">Class details</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Class</label>
                <input data-testid="attendance-class" value={className} onChange={(e) => setClassName(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Subject</label>
                <input data-testid="attendance-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Teacher</label>
                <input value={teacher} onChange={(e) => setTeacher(e.target.value)} placeholder="Ms. Sharma" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Month</label>
                <input value={month} onChange={(e) => setMonth(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Working days</label>
                <input data-testid="attendance-days" type="number" min={1} max={31} value={days} onChange={(e) => setDays(Number(e.target.value))} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
              </div>
            </div>
          </PremiumCard>

          <PremiumCard className="p-6 lg:col-span-2" hover={false}>
            <h3 className="font-heading text-lg font-semibold mb-2">Student roster</h3>
            <p className="text-xs text-slate-500 mb-3">Paste names one per line or comma-separated.</p>
            <textarea
              data-testid="attendance-names"
              value={namesRaw}
              onChange={(e) => setNamesRaw(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 font-sans text-sm"
            />
            <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
              <span className="text-xs text-slate-500">{names.length} student{names.length !== 1 && "s"}</span>
              <PremiumButton icon={Printer} onClick={printSheet} data-testid="attendance-print">
                Print / PDF
              </PremiumButton>
            </div>
          </PremiumCard>
        </div>

        {/* Printable sheet */}
        <div className="mt-10 print:mt-0">
          <PremiumCard className="p-8 print:shadow-none print:border-0" hover={false}>
            <div className="flex items-center justify-between flex-wrap gap-3 pb-4 border-b border-slate-200">
              <div>
                <h2 className="font-heading text-2xl font-bold text-[#111111]">{className} · {subject}</h2>
                <p className="text-sm text-slate-600 mt-1">Monthly Attendance · {month}{teacher && ` · ${teacher}`}</p>
              </div>
              <span className="font-heading text-sm font-bold text-[#A855F7]">MarkeeMix</span>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full border border-slate-200 text-xs print:text-[10px]">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left p-2 border-r border-slate-200 w-12">#</th>
                    <th className="text-left p-2 border-r border-slate-200 min-w-[140px]">Student name</th>
                    {dayList.map((d) => (
                      <th key={d} className="p-2 border-r border-slate-200 last:border-0 w-7 text-center">{d}</th>
                    ))}
                    <th className="p-2 w-12 text-center">P/A</th>
                  </tr>
                </thead>
                <tbody>
                  {names.map((n, i) => (
                    <tr key={i} className="border-t border-slate-200">
                      <td className="p-2 border-r border-slate-200">{i + 1}</td>
                      <td className="p-2 border-r border-slate-200 font-medium">{n}</td>
                      {dayList.map((d) => <td key={d} className="border-r border-slate-200 last:border-0 h-8"></td>)}
                      <td className="border-r border-slate-200"></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-slate-400">Mark P = Present, A = Absent, L = Late.</p>
          </PremiumCard>
        </div>
      </div>
      <div className="print:hidden"><PremiumFooter /></div>
      <style>{`@media print { body { background: white; } .print\\:hidden { display: none !important; } .print\\:py-2 { padding-top: .5rem !important; padding-bottom: .5rem !important; } .print\\:shadow-none { box-shadow: none !important; } .print\\:border-0 { border: 0 !important; } @page { size: A4 landscape; margin: 10mm; } }`}</style>
    </div>
  );
}

// ─── Certificate Generator ────────────────────────────────────────────
function CertificateGenerator() {
  const navigate = useNavigate();
  const [namesRaw, setNamesRaw] = useState("Aarav Sharma\nNeha Patel\nRohan Mehta");
  const [course, setCourse] = useState("Excellence in Science Project Showcase 2026");
  const [issuer, setIssuer] = useState("MarkeeMix Academy");
  const [signatory, setSignatory] = useState("Ms. Anita Kapoor");
  const [signatoryRole, setSignatoryRole] = useState("Principal");
  const [date, setDate] = useState(new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }));

  const names = parseCSVNames(namesRaw);

  const printAll = () => window.print();

  return (
    <div className="min-h-screen bg-white font-sans" data-testid="teaching-tool-certificate">
      <PremiumNav />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 print:py-0">
        <button
          onClick={() => navigate("/teaching")}
          className="print:hidden inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#A855F7] mb-6"
          data-testid="suite-back"
        >
          <ArrowLeft className="w-4 h-4"/> Back to Teaching Suite
        </button>

        <div className="print:hidden">
          <SectionHeading
            eyebrow={<><Award className="w-3 h-3 inline -mt-0.5 mr-1"/> Teaching Suite</>}
            title="Certificate Generator"
            subtitle="Bulk-generate branded certificates. Add a list of names → 1-click print."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8 print:hidden">
          <PremiumCard className="p-6 lg:col-span-2" hover={false}>
            <h3 className="font-heading text-lg font-semibold mb-4">Certificate details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Course / Award</label>
                <input data-testid="cert-course" value={course} onChange={(e) => setCourse(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Issuer</label>
                <input value={issuer} onChange={(e) => setIssuer(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Signatory name</label>
                <input value={signatory} onChange={(e) => setSignatory(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Signatory role</label>
                <input value={signatoryRole} onChange={(e) => setSignatoryRole(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Date</label>
                <input value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Recipient names</label>
                <textarea data-testid="cert-names" value={namesRaw} onChange={(e) => setNamesRaw(e.target.value)} rows={6} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                <p className="mt-1 text-xs text-slate-500">One per line or comma-separated. {names.length} certificate{names.length !== 1 && "s"} will be generated.</p>
              </div>
            </div>
          </PremiumCard>

          <PremiumCard className="p-6 lg:col-span-1 flex flex-col" hover={false}>
            <h3 className="font-heading text-lg font-semibold mb-2">Print all</h3>
            <p className="text-sm text-slate-500 mb-4">Each certificate prints on its own A4 landscape page. Use browser&apos;s print dialog to save as PDF.</p>
            <PremiumButton icon={Printer} onClick={printAll} data-testid="cert-print" className="mt-auto">
              Print {names.length} certificate{names.length !== 1 && "s"}
            </PremiumButton>
          </PremiumCard>
        </div>

        {/* Printable certificates */}
        <div className="mt-10 space-y-8 print:mt-0 print:space-y-0">
          {names.map((n, i) => (
            <div key={i} className="cert-page" data-testid={`cert-${i}`}>
              <PremiumCard className="p-12 sm:p-16 text-center print:shadow-none print:border-0 relative overflow-hidden" hover={false}>
                <div className="absolute top-0 left-0 w-32 h-32 -translate-x-16 -translate-y-16 rounded-full" style={{ background: "radial-gradient(circle, rgba(168,85,247,0.18), transparent 70%)" }}/>
                <div className="absolute bottom-0 right-0 w-32 h-32 translate-x-16 translate-y-16 rounded-full" style={{ background: "radial-gradient(circle, rgba(168,85,247,0.18), transparent 70%)" }}/>
                <div className="relative">
                  <span className="inline-block text-[11px] tracking-[0.3em] uppercase font-bold text-[#A855F7]">{issuer}</span>
                  <h1 className="mt-6 font-heading text-5xl sm:text-6xl font-bold tracking-tight text-[#111111]">Certificate of Excellence</h1>
                  <p className="mt-6 text-sm text-slate-500 uppercase tracking-widest">This is to certify that</p>
                  <p className="mt-3 font-heading text-4xl sm:text-5xl font-semibold text-[#A855F7]">{n}</p>
                  <p className="mt-6 text-base text-slate-700 max-w-2xl mx-auto leading-relaxed">has successfully demonstrated outstanding performance in</p>
                  <p className="mt-2 font-heading text-2xl font-semibold text-[#111111]">{course}</p>
                  <div className="mt-12 flex items-end justify-between flex-wrap gap-6 max-w-2xl mx-auto">
                    <div className="text-left">
                      <div className="font-heading text-base font-bold text-[#111111] border-b border-slate-300 pb-1">{signatory}</div>
                      <div className="text-xs text-slate-500 mt-1">{signatoryRole}</div>
                    </div>
                    <div className="text-left">
                      <div className="font-heading text-base font-bold text-[#111111] border-b border-slate-300 pb-1">{date}</div>
                      <div className="text-xs text-slate-500 mt-1">Date issued</div>
                    </div>
                  </div>
                  <div className="mt-10 font-heading text-xs font-bold text-slate-400 tracking-widest">MARKEE<span className="text-[#A855F7]">MIX</span></div>
                </div>
              </PremiumCard>
            </div>
          ))}
        </div>
      </div>
      <div className="print:hidden"><PremiumFooter /></div>
      <style>{`@media print { body { background: white; } .print\\:hidden { display: none !important; } .print\\:mt-0 { margin-top: 0 !important; } .print\\:space-y-0 > * + * { margin-top: 0 !important; } .print\\:shadow-none { box-shadow: none !important; } .print\\:border-0 { border: 0 !important; } .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; } .cert-page { page-break-after: always; } @page { size: A4 landscape; margin: 10mm; } }`}</style>
    </div>
  );
}

export default function TeachingTemplate() {
  const { pathname } = useLocation();
  const tool = pathname.split("/").filter(Boolean).pop();
  if (tool === "attendance") return <AttendanceSheet />;
  if (tool === "certificate") return <CertificateGenerator />;
  return null;
}
